import { getTracer } from '@/otel/tracer';

import { context, Span, SpanOptions, trace } from '@opentelemetry/api';
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useRef,
} from 'react';

type SpansContextType = {
  getSpan: (key: string) => Span | undefined;
  getOrCreateSpan: (args: {
    key: string;
    spanName?: string;
    options?: SpanOptions;
  }) => Span;
  withSpanContext: (span: Span, callback: () => void) => void;
  endSpan: (key: string) => void;
};

const defaultSpansContext: SpansContextType = {
  getSpan: (_: string) => {
    console.log('ActiveSpanContextValue not provided');
    return undefined;
  },
  getOrCreateSpan: () => {
    throw new Error('ActiveSpanContextValue not provided');
  },
  withSpanContext: () => {
    console.error('ActiveSpanContextValue not provided');
  },
  endSpan: () => {
    console.error('ActiveSpanContextValue not provided');
  },
};

export const SpansContext =
  createContext<SpansContextType>(defaultSpansContext);

export const SpansProvider: FC<PropsWithChildren> = ({ children }) => {
  const spansRef = useRef<Map<string, Span>>(new Map());

  const getSpan = (key: string): Span | undefined => {
    return spansRef.current.get(key);
  };

  const getOrCreateSpan = useCallback(
    ({
      key,
      spanName,
      options,
    }: {
      key: string;
      spanName?: string;
      options?: SpanOptions;
    }): Span => {
      const maybeExistingSpan = spansRef.current.get(key);
      if (maybeExistingSpan) {
        return maybeExistingSpan;
      }

      const tracer = getTracer();
      const span = tracer.startSpan(spanName || key, options);
      span.setAttribute('host', window.location.host);
      spansRef.current.set(key, span);
      return span;
    },
    [],
  );

  const withSpanContext = useCallback((span: Span, callback: () => void) => {
    context.with(trace.setSpan(context.active(), span), callback);
  }, []);

  const endSpan = useCallback((key: string) => {
    const span = spansRef.current.get(key);
    if (span) {
      span.end();
      spansRef.current.delete(key);
    }
  }, []);

  return (
    <SpansContext.Provider
      value={{ getSpan, getOrCreateSpan, withSpanContext, endSpan }}
    >
      {children}
    </SpansContext.Provider>
  );
};
